'use client';

import { useState, useEffect } from 'react';
import { useTranslations } from 'next-intl';

export default function CreditCardCalculator() {
  const t = useTranslations("tools.creditCardCalculator");
  const [screenSize, setScreenSize] = useState('mobile');

  // Calculator state
  const [balance, setBalance] = useState(8000);
  const [rate, setRate] = useState(18);
  const [payoffOption, setPayoffOption] = useState<'fixedPayment' | 'fixedTime'>('fixedPayment');
  const [fixedPayment, setFixedPayment] = useState(200);
  const [years, setYears] = useState(2);
  const [months, setMonths] = useState(0);
  const [results, setResults] = useState<any>(null);
  const [error, setError] = useState('');

  useEffect(() => {
    const updateScreenSize = () => {
      const width = window.innerWidth;
      if (width >= 1024) {
        setScreenSize('desktop');
      } else if (width >= 768) {
        setScreenSize('tablet');
      } else {
        setScreenSize('mobile');
      }
    };

    updateScreenSize();
    window.addEventListener('resize', updateScreenSize);
    return () => window.removeEventListener('resize', updateScreenSize);
  }, []);

  const getResponsiveStyle = (baseStyle: any, mobileOverrides?: any, tabletOverrides?: any, desktopOverrides?: any) => {
    let style = { ...baseStyle };

    if (screenSize === 'mobile' && mobileOverrides) {
      style = { ...style, ...mobileOverrides };
    } else if (screenSize === 'tablet' && tabletOverrides) {
      style = { ...style, ...tabletOverrides };
    } else if (screenSize === 'desktop' && desktopOverrides) {
      style = { ...style, ...desktopOverrides };
    }

    return style;
  };

  const colors = {
    primary: '#ff5a70',
    primaryHover: '#f63b3b',
    secondary: '#ffc400',
    accent: '#ff6b95',
    textPrimary: '#2d3748',
    textSecondary: '#4a5568',
    glassBg: 'rgba(255, 255, 255, 0.6)',
    glassBorder: 'rgba(255, 200, 180, 0.2)',
  };

  const handleQuickLink = (percent: number) => {
    let payment = balance * (percent / 100);
    if (percent === 1) {
      payment = balance * 0.01 + (balance * rate / 1200);
    }
    payment = Math.max(payment, 15);
    if (balance < 15) payment = balance;
    setFixedPayment(parseFloat(payment.toFixed(2)));
  };

  const handleCalculate = (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (!balance || balance <= 0) {
      setError(t('errorInvalidBalance'));
      return;
    }

    if (rate < 0) {
      setError(t('errorInvalidRate'));
      return;
    }

    let monthlyPayment: number;
    let totalMonths: number;

    if (payoffOption === 'fixedPayment') {
      if (!fixedPayment || fixedPayment <= 0) {
        setError(t('errorInvalidPayment'));
        return;
      }

      const monthlyRate = rate / 1200;
      const minPayment = balance * monthlyRate;

      if (fixedPayment <= minPayment) {
        setError(t('errorPaymentTooLow') + ' ($' + minPayment.toFixed(2) + ')');
        return;
      }

      if (monthlyRate === 0) {
        totalMonths = Math.ceil(balance / fixedPayment);
      } else {
        totalMonths = Math.ceil(-Math.log(1 - (balance * monthlyRate / fixedPayment)) / Math.log(1 + monthlyRate));
      }
      monthlyPayment = fixedPayment;

    } else {
      totalMonths = years * 12 + months;

      if (totalMonths <= 0) {
        setError(t('errorInvalidTimeframe'));
        return;
      }

      const monthlyRate = rate / 1200;

      if (monthlyRate === 0) {
        monthlyPayment = balance / totalMonths;
      } else {
        monthlyPayment = balance * (monthlyRate * Math.pow(1 + monthlyRate, totalMonths)) / (Math.pow(1 + monthlyRate, totalMonths) - 1);
      }
    }

    const totalPaid = monthlyPayment * totalMonths;
    const totalInterest = totalPaid - balance;
    const interestPercent = (totalInterest / totalPaid * 100);

    const today = new Date();
    const payoffDate = new Date(today.setMonth(today.getMonth() + totalMonths));
    const payoffDateStr = payoffDate.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });

    const yearsCount = Math.floor(totalMonths / 12);
    const remainingMonths = totalMonths % 12;
    let timeStr = '';
    if (yearsCount > 0) timeStr += yearsCount + (yearsCount === 1 ? ' year' : ' years');
    if (remainingMonths > 0) {
      if (timeStr) timeStr += ' ';
      timeStr += remainingMonths + (remainingMonths === 1 ? ' month' : ' months');
    }

    setResults({
      monthlyPayment: monthlyPayment.toFixed(2),
      payoffTime: timeStr || '0 months',
      totalInterest: totalInterest.toFixed(2),
      totalPaid: totalPaid.toFixed(2),
      principalAmount: balance.toFixed(2),
      interestPercent: interestPercent.toFixed(1),
      payoffDate: payoffDateStr,
    });
  };

  const containerStyle = getResponsiveStyle(
    { maxWidth: '1200px', margin: '0 auto', position: 'relative' as const, zIndex: 1 },
    { padding: '1rem' },
    { padding: '1.5rem' },
    { padding: '2rem' }
  );

  const headerStyle = getResponsiveStyle(
    { textAlign: 'center' as const, marginBottom: '2rem' },
    {},
    { marginBottom: '2.5rem' },
    { marginBottom: '3rem' }
  );

  const h1Style = getResponsiveStyle(
    { fontWeight: 700, color: colors.textPrimary, marginBottom: '1rem', lineHeight: 1.2 },
    { fontSize: '2rem' },
    { fontSize: '2.5rem' },
    { fontSize: '3rem' }
  );

  const summaryTextStyle = getResponsiveStyle(
    { color: colors.textSecondary, lineHeight: 1.6 },
    { fontSize: '1rem' },
    { fontSize: '1.125rem' },
    { fontSize: '1.25rem' }
  );

  const calculatorToolStyle = getResponsiveStyle(
    {
      background: 'linear-gradient(135deg, rgba(240, 249, 255, 0.9) 0%, rgba(224, 242, 254, 0.9) 100%)',
      marginBottom: '2rem',
      borderRadius: '1rem',
    },
    { padding: '1.5rem 1rem' },
    { padding: '2rem 1.5rem', borderRadius: '1.25rem' },
    { padding: '3rem 2rem', borderRadius: '1.5rem' }
  );

  const toolContainerStyle = getResponsiveStyle(
    {
      maxWidth: '100%',
      margin: '0 auto',
      background: 'white',
      borderRadius: '1rem',
      boxShadow: '0 10px 40px rgba(0, 0, 0, 0.1)',
      overflow: 'hidden' as const,
    },
    {},
    { maxWidth: '700px' },
    { maxWidth: '900px' }
  );

  const toolHeaderStyle = getResponsiveStyle(
    {
      background: 'linear-gradient(135deg, #2563eb 0%, #7c3aed 100%)',
      color: 'white',
      textAlign: 'center' as const,
    },
    { padding: '1.5rem' },
    { padding: '1.75rem' },
    { padding: '2rem' }
  );

  const toolHeaderH2Style = getResponsiveStyle(
    { margin: 0, fontWeight: 700 },
    { fontSize: '1.5rem' },
    { fontSize: '1.65rem' },
    { fontSize: '1.8rem' }
  );

  const calculatorFormStyle = getResponsiveStyle(
    {},
    { padding: '1.5rem' },
    { padding: '1.75rem' },
    { padding: '2rem' }
  );

  const formGroupStyle = getResponsiveStyle(
    {},
    { marginBottom: '1.25rem' },
    { marginBottom: '1.375rem' },
    { marginBottom: '1.5rem' }
  );

  const labelStyle = {
    display: 'block',
    marginBottom: '0.5rem',
    fontWeight: 600,
    color: '#1e293b',
    fontSize: '0.95rem',
  };

  const inputStyle = {
    width: '100%',
    padding: '0.75rem',
    border: '2px solid #e2e8f0',
    borderRadius: '0.5rem',
    fontSize: '1rem',
    transition: 'all 0.3s ease',
    boxSizing: 'border-box' as const,
  };

  const inputGroupStyle = {
    position: 'relative' as const,
  };

  const inputPrefixStyle = {
    position: 'absolute' as const,
    left: '0.75rem',
    top: '50%',
    transform: 'translateY(-50%)',
    color: '#64748b',
    fontWeight: 500,
  };

  const inputSuffixStyle = {
    position: 'absolute' as const,
    right: '0.75rem',
    top: '50%',
    transform: 'translateY(-50%)',
    color: '#64748b',
    fontWeight: 500,
  };

  const radioGroupStyle = getResponsiveStyle(
    {
      background: '#f8fafc',
      borderRadius: '0.5rem',
      border: '2px solid #e2e8f0',
    },
    { padding: '1rem' },
    { padding: '1.25rem' },
    { padding: '1.5rem' }
  );

  const radioOptionStyle = {
    marginBottom: '1rem',
  };

  const radioLabelStyle = {
    display: 'flex',
    alignItems: 'center',
    cursor: 'pointer',
    fontWeight: 500,
  };

  const nestedInputStyle = {
    marginLeft: '1.75rem',
    marginTop: '0.75rem',
    display: 'none',
  };

  const nestedInputActiveStyle = {
    ...nestedInputStyle,
    display: 'block',
  };

  const quickLinksStyle = {
    marginTop: '0.5rem',
    fontSize: '0.85rem',
  };

  const quickLinkStyle = {
    color: '#2563eb',
    textDecoration: 'none',
    marginRight: '0.5rem',
    cursor: 'pointer',
  };

  const timeInputsStyle = getResponsiveStyle(
    { display: 'flex', alignItems: 'center' },
    { flexDirection: 'column' as const, gap: '0.75rem' },
    { flexDirection: 'row' as const, gap: '1rem' },
    { gap: '1rem' }
  );

  const timeInputGroupStyle = {
    flex: 1,
    display: 'flex',
    alignItems: 'center',
    gap: '0.5rem',
  };

  const calculateBtnStyle = getResponsiveStyle(
    {
      width: '100%',
      background: 'linear-gradient(135deg, #2563eb 0%, #7c3aed 100%)',
      color: 'white',
      border: 'none',
      borderRadius: '0.5rem',
      fontWeight: 600,
      cursor: 'pointer',
      transition: 'all 0.3s ease',
      marginTop: '1rem',
    },
    { padding: '0.875rem', fontSize: '1rem' },
    { padding: '1rem', fontSize: '1.05rem' },
    { padding: '1rem', fontSize: '1.1rem' }
  );

  const errorMessageStyle = {
    background: '#fef2f2',
    color: '#991b1b',
    padding: '1rem',
    borderRadius: '0.5rem',
    marginTop: '1rem',
    borderLeft: '4px solid #dc2626',
    display: error ? 'block' : 'none',
  };

  const resultsStyle = getResponsiveStyle(
    {
      background: 'linear-gradient(135deg, #f0fdf4 0%, #dcfce7 100%)',
      borderTop: '3px solid #10b981',
      display: results ? 'block' : 'none',
    },
    { padding: '1.5rem' },
    { padding: '1.75rem' },
    { padding: '2rem' }
  );

  const resultsH3Style = getResponsiveStyle(
    {
      margin: '0 0 1.5rem 0',
      color: '#065f46',
      display: 'flex',
      alignItems: 'center',
      gap: '0.5rem',
    },
    { fontSize: '1.35rem' },
    { fontSize: '1.425rem' },
    { fontSize: '1.5rem' }
  );

  const resultGridStyle = getResponsiveStyle(
    {
      display: 'grid',
      gap: '1rem',
      marginBottom: '1.5rem',
    },
    { gridTemplateColumns: '1fr' },
    { gridTemplateColumns: 'repeat(2, 1fr)' },
    { gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))' }
  );

  const resultCardStyle = {
    background: 'white',
    padding: '1.25rem',
    borderRadius: '0.75rem',
    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.05)',
  };

  const resultLabelStyle = {
    display: 'block',
    fontSize: '0.85rem',
    color: '#64748b',
    marginBottom: '0.5rem',
    fontWeight: 500,
  };

  const resultValueStyle = {
    display: 'block',
    fontSize: '1.75rem',
    fontWeight: 700,
    color: '#0f172a',
  };

  const paymentScheduleStyle = {
    background: 'white',
    padding: '1.5rem',
    borderRadius: '0.75rem',
    marginTop: '1.5rem',
  };

  const scheduleItemStyle = {
    display: 'flex',
    justifyContent: 'space-between',
    padding: '0.75rem 0',
    borderBottom: '1px solid #e2e8f0',
  };

  const glassCardStyle = getResponsiveStyle(
    {
      background: 'rgba(255, 255, 255, 0.6)',
      backdropFilter: 'blur(20px) saturate(180%)',
      WebkitBackdropFilter: 'blur(20px) saturate(180%)',
      border: '1px solid rgba(255, 200, 180, 0.2)',
      borderRadius: '1rem',
      boxShadow: '0 8px 32px 0 rgba(255, 90, 112, 0.08)',
    },
    { padding: '1.5rem', marginBottom: '1.5rem' },
    { padding: '2rem', marginBottom: '2rem' },
    { padding: '2.5rem', marginBottom: '2.5rem' }
  );

  const h2Style = getResponsiveStyle(
    { fontWeight: 600, color: colors.textPrimary, marginBottom: '1rem' },
    { fontSize: '1.5rem' },
    { fontSize: '1.75rem' },
    { fontSize: '2rem' }
  );

  const paragraphStyle = getResponsiveStyle(
    { lineHeight: 1.6, color: colors.textSecondary, marginBottom: '1rem' },
    { fontSize: '1rem' },
    { fontSize: '1.125rem', lineHeight: 1.7 },
    { fontSize: '1.25rem', lineHeight: 1.8 }
  );

  const listStyle = {
    ...paragraphStyle,
    paddingLeft: '1.5rem',
  };

  const listItemStyle = {
    marginBottom: '0.75rem',
  };

  const strongStyle = {
    color: colors.textPrimary,
    fontWeight: 600,
  };

  const faqItemStyle = {
    marginBottom: '1.5rem',
  };

  const faqQuestionStyle = {
    ...strongStyle,
    display: 'block',
    marginBottom: '0.5rem',
    fontSize: '1.1rem',
  };

  const referenceStyle = {
    fontSize: '0.9rem',
    color: colors.textSecondary,
    lineHeight: 1.6,
    marginBottom: '0.5rem',
  };

  return (
    <div style={containerStyle}>
      <header style={headerStyle}>
        <h1 style={h1Style}>{t('title')}</h1>
        <p style={summaryTextStyle}>{t('subtitle')}</p>
      </header>

      <section style={calculatorToolStyle}>
        <div style={toolContainerStyle}>
          <div style={toolHeaderStyle}>
            <h2 style={toolHeaderH2Style}>{t('toolTitle')}</h2>
          </div>

          <form style={calculatorFormStyle} onSubmit={handleCalculate}>
            <div style={formGroupStyle}>
              <label style={labelStyle} htmlFor="balance">{t('balanceLabel')}</label>
              <div style={inputGroupStyle}>
                <span style={inputPrefixStyle}>$</span>
                <input
                  type="number"
                  id="balance"
                  step="0.01"
                  min="0"
                  value={balance}
                  onChange={(e) => setBalance(parseFloat(e.target.value))}
                  style={{ ...inputStyle, paddingLeft: '2rem' }}
                  required
                />
              </div>
            </div>

            <div style={formGroupStyle}>
              <label style={labelStyle} htmlFor="rate">{t('rateLabel')}</label>
              <div style={inputGroupStyle}>
                <input
                  type="number"
                  id="rate"
                  step="0.01"
                  min="0"
                  max="100"
                  value={rate}
                  onChange={(e) => setRate(parseFloat(e.target.value))}
                  style={{ ...inputStyle, paddingRight: '2.5rem' }}
                  required
                />
                <span style={inputSuffixStyle}>%</span>
              </div>
            </div>

            <div style={formGroupStyle}>
              <label style={labelStyle}>{t('payoffOptionLabel')}</label>
              <div style={radioGroupStyle}>
                <div style={radioOptionStyle}>
                  <label style={radioLabelStyle}>
                    <input
                      type="radio"
                      name="payoffOption"
                      value="fixedPayment"
                      checked={payoffOption === 'fixedPayment'}
                      onChange={() => setPayoffOption('fixedPayment')}
                      style={{ width: 'auto', marginRight: '0.5rem', cursor: 'pointer' }}
                    />
                    {t('fixedPaymentOption')}
                  </label>
                  <div style={payoffOption === 'fixedPayment' ? nestedInputActiveStyle : nestedInputStyle}>
                    <div style={inputGroupStyle}>
                      <span style={inputPrefixStyle}>$</span>
                      <input
                        type="number"
                        id="fixedPayment"
                        step="0.01"
                        min="0"
                        value={fixedPayment}
                        onChange={(e) => setFixedPayment(parseFloat(e.target.value))}
                        style={{ ...inputStyle, paddingLeft: '2rem' }}
                      />
                      <span style={{ marginLeft: '0.5rem', color: '#64748b' }}>{t('perMonth')}</span>
                    </div>
                    <div style={quickLinksStyle}>
                      {t('quickLinksPrefix')}
                      <a style={quickLinkStyle} onClick={() => handleQuickLink(1)}>{t('quickLink1')}</a>
                      <a style={quickLinkStyle} onClick={() => handleQuickLink(2)}>2%</a>
                      <a style={quickLinkStyle} onClick={() => handleQuickLink(3)}>3%</a>
                      <a style={quickLinkStyle} onClick={() => handleQuickLink(4)}>4%</a>
                      <a style={quickLinkStyle} onClick={() => handleQuickLink(5)}>5%</a>
                    </div>
                  </div>
                </div>

                <div style={{ ...radioOptionStyle, marginBottom: 0 }}>
                  <label style={radioLabelStyle}>
                    <input
                      type="radio"
                      name="payoffOption"
                      value="fixedTime"
                      checked={payoffOption === 'fixedTime'}
                      onChange={() => setPayoffOption('fixedTime')}
                      style={{ width: 'auto', marginRight: '0.5rem', cursor: 'pointer' }}
                    />
                    {t('fixedTimeOption')}
                  </label>
                  <div style={payoffOption === 'fixedTime' ? nestedInputActiveStyle : nestedInputStyle}>
                    <div style={timeInputsStyle}>
                      <div style={timeInputGroupStyle}>
                        <input
                          type="number"
                          id="years"
                          min="0"
                          value={years}
                          onChange={(e) => setYears(parseInt(e.target.value))}
                          style={{ ...inputStyle, width: screenSize === 'mobile' ? '100%' : '80px' }}
                        />
                        <span>{t('years')}</span>
                      </div>
                      <div style={timeInputGroupStyle}>
                        <input
                          type="number"
                          id="months"
                          min="0"
                          max="11"
                          value={months}
                          onChange={(e) => setMonths(parseInt(e.target.value))}
                          style={{ ...inputStyle, width: screenSize === 'mobile' ? '100%' : '80px' }}
                        />
                        <span>{t('months')}</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <button type="submit" style={calculateBtnStyle}>
              {t('calculateButton')}
            </button>

            <div style={errorMessageStyle}>{error}</div>
          </form>

          {results && (
            <div style={resultsStyle}>
              <h3 style={resultsH3Style}>{t('resultsTitle')}</h3>
              <div style={resultGridStyle}>
                <div style={resultCardStyle}>
                  <span style={resultLabelStyle}>{t('monthlyPaymentLabel')}</span>
                  <span style={{ ...resultValueStyle, color: '#2563eb' }}>${results.monthlyPayment}</span>
                </div>
                <div style={resultCardStyle}>
                  <span style={resultLabelStyle}>{t('payoffTimeLabel')}</span>
                  <span style={resultValueStyle}>{results.payoffTime}</span>
                </div>
                <div style={resultCardStyle}>
                  <span style={resultLabelStyle}>{t('totalInterestLabel')}</span>
                  <span style={{ ...resultValueStyle, color: '#f59e0b' }}>${results.totalInterest}</span>
                </div>
                <div style={resultCardStyle}>
                  <span style={resultLabelStyle}>{t('totalPaidLabel')}</span>
                  <span style={resultValueStyle}>${results.totalPaid}</span>
                </div>
              </div>

              <div style={paymentScheduleStyle}>
                <h4 style={{ margin: '0 0 1rem 0', color: '#1e293b', fontSize: '1.1rem' }}>{t('insightsTitle')}</h4>
                <div style={scheduleItemStyle}>
                  <span>{t('principalAmountLabel')}</span>
                  <strong>${results.principalAmount}</strong>
                </div>
                <div style={scheduleItemStyle}>
                  <span>{t('interestPercentLabel')}</span>
                  <strong>{results.interestPercent}%</strong>
                </div>
                <div style={{ ...scheduleItemStyle, borderBottom: 'none' }}>
                  <span>{t('payoffDateLabel')}</span>
                  <strong>{results.payoffDate}</strong>
                </div>
              </div>
            </div>
          )}
        </div>
      </section>

      <section style={glassCardStyle}>
        <h2 style={h2Style}>{t('introTitle')}</h2>
        <p style={paragraphStyle}>{t('introParagraph1')}</p>
        <p style={paragraphStyle}>{t('introParagraph2')}</p>
        <p style={paragraphStyle}>{t('introParagraph3')}</p>
      </section>

      <section style={glassCardStyle}>
        <h2 style={h2Style}>{t('howToUseTitle')}</h2>
        <ol style={listStyle}>
          <li style={listItemStyle}><strong style={strongStyle}>{t('step1Title')}</strong> {t('step1Description')}</li>
          <li style={listItemStyle}><strong style={strongStyle}>{t('step2Title')}</strong> {t('step2Description')}</li>
          <li style={listItemStyle}><strong style={strongStyle}>{t('step3Title')}</strong> {t('step3Description')}</li>
          <li style={listItemStyle}><strong style={strongStyle}>{t('step4Title')}</strong> {t('step4Description')}</li>
        </ol>
      </section>

      <section style={glassCardStyle}>
        <h2 style={h2Style}>{t('insightsSectionTitle')}</h2>
        <p style={paragraphStyle}>{t('insightsParagraph1')}</p>
        <ul style={listStyle}>
          <li style={listItemStyle}><strong style={strongStyle}>{t('insightCompoundingTitle')}</strong> {t('insightCompoundingDescription')}</li>
          <li style={listItemStyle}><strong style={strongStyle}>{t('insightMinimumTitle')}</strong> {t('insightMinimumDescription')}</li>
          <li style={listItemStyle}><strong style={strongStyle}>{t('insightAvalancheTitle')}</strong> {t('insightAvalancheDescription')}</li>
          <li style={listItemStyle}><strong style={strongStyle}>{t('insightSnowballTitle')}</strong> {t('insightSnowballDescription')}</li>
          <li style={listItemStyle}><strong style={strongStyle}>{t('insightUtilizationTitle')}</strong> {t('insightUtilizationDescription')}</li>
          <li style={listItemStyle}><strong style={strongStyle}>{t('insightTransferTitle')}</strong> {t('insightTransferDescription')}</li>
        </ul>
      </section>

      <section style={glassCardStyle}>
        <h2 style={h2Style}>{t('detailsTitle')}</h2>
        <p style={paragraphStyle}>{t('detailsParagraph1')}</p>
        <p style={paragraphStyle}>{t('detailsParagraph2')}</p>
        <p style={paragraphStyle}>{t('detailsParagraph3')}</p>
        <p style={paragraphStyle}>{t('detailsParagraph4')}</p>
      </section>

      <section style={glassCardStyle}>
        <h2 style={h2Style}>{t('faqTitle')}</h2>
        <div style={faqItemStyle}>
          <span style={faqQuestionStyle}>{t('faq1Question')}</span>
          <p style={paragraphStyle}>{t('faq1Answer')}</p>
        </div>
        <div style={faqItemStyle}>
          <span style={faqQuestionStyle}>{t('faq2Question')}</span>
          <p style={paragraphStyle}>{t('faq2Answer')}</p>
        </div>
        <div style={faqItemStyle}>
          <span style={faqQuestionStyle}>{t('faq3Question')}</span>
          <p style={paragraphStyle}>{t('faq3Answer')}</p>
        </div>
        <div style={faqItemStyle}>
          <span style={faqQuestionStyle}>{t('faq4Question')}</span>
          <p style={paragraphStyle}>{t('faq4Answer')}</p>
        </div>
        <div style={faqItemStyle}>
          <span style={faqQuestionStyle}>{t('faq5Question')}</span>
          <p style={paragraphStyle}>{t('faq5Answer')}</p>
        </div>
      </section>

      <section style={glassCardStyle}>
        <h2 style={h2Style}>{t('referencesTitle')}</h2>
        <p style={referenceStyle}>{t('reference1')}</p>
        <p style={referenceStyle}>{t('reference2')}</p>
        <p style={referenceStyle}>{t('reference3')}</p>
        <p style={referenceStyle}>{t('reference4')}</p>
        <p style={referenceStyle}>{t('reference5')}</p>
      </section>
    </div>
  );
}